import "./chartSetup";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { ChartData, ChartOptions } from "chart.js";
import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { useFinance } from "../../FinanceContext";
import { moneyShort, sum } from "../../data/model";
import { SCENARIOS } from "../../data/seedData";

export function ForecastChart({ approved, fc }: { approved: number; fc: number }) {
  const { model, C } = useFinance();
  const theme = useTheme();
  const text = theme.palette.mode === "dark" ? "#9aa1b8" : "#69708a";

  const { labels, annual } = useMemo(() => {
    const base = model.items.reduce((s, i) => s + sum(i.planned), 0);
    const scen = Object.keys(SCENARIOS);
    return {
      labels: [...scen, "Run-rate"],
      annual: [
        ...scen.map((s) =>
          Math.round(base * (SCENARIOS[s as keyof typeof SCENARIOS] ?? 1) * C.rate),
        ),
        Math.round(fc * C.rate),
      ],
    };
  }, [model, C, fc]);

  const data = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Annual",
        data: annual,
        backgroundColor: labels.map((l) => (l === "Run-rate" ? "#16a34a" : "#8b5cf6")),
        borderRadius: 5,
      },
      {
        type: "line" as const,
        label: "Approved",
        data: labels.map(() => Math.round(approved * C.rate)),
        borderColor: "#dc2626",
        borderDash: [6, 4],
        pointRadius: 0,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { color: text } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.y), C.symbol)}`,
        },
      },
    },
    scales: {
      y: { ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) } },
      x: { ticks: { color: text } },
    },
  };
  return (
    <Box sx={{ position: "relative", height: 230 }}>
      {/* Mixed bar+line chart: chart.js supports per-dataset `type` overrides at runtime,
          but its TS types don't model a "bar chart with one line dataset" cleanly. */}
      <Chart type="bar" data={data as unknown as ChartData<"bar">} options={options} />
    </Box>
  );
}
